import React, { Component } from "react";
import { observer } from "mobx-react";
import {
  CoursePreviewResource,
  QuotationResource,
} from "../../stores/CoursePreviewStore/Types";
import { CurrentUserResource } from "../../stores/CurrentUserStore/Types";

import { NewPricing } from "../Home/components";
import PricingStore from "../../stores/PricingStore";
import Landing from "./components/Landing";
import PurchaseNavbar from "./components/PurchaseNavbar";
import ThoughtLeaders from "./components/ThoughtLeaders";
import CourseChapters from "./components/CourseChapters";
import Reviews from "./components/Reviews";
import FeaturedCourses from "./components/FeaturedCourses";
import HeroTrustStrip from "./components/HeroTrustStrip";
import Promise from "./components/Promise";
import Description from "./components/Description";
import Proof from "./components/Proof";
import Compare from "./components/Compare";
import Guarantee from "./components/Guarantee";
import Faq from "./components/Faq";
import FinalCta from "./components/FinalCta";
import PromoBar from "../PromoBar";
import SignupBox from "../RoryQuotes/SignupBox";

// The courses where a Monday email of a Rory quote and a behavioural bias
// is the natural "not yet" offer.
const BEHAVIOURAL_COURSES = [
  "behavioural-economics",
  "behavioural-science",
  "behavioural-science-for-effective-messaging",
  "behavioural-science-for-brands",
  "behavioural-science-for-life",
  "the-art-of-behavioural-investing",
  "nudgestock",
];

interface Props {
  mode?: string;
  course: CoursePreviewResource;
  refresh: (slug: string) => void;
  handleShowSignInSideBar: () => void;
  currentUser: CurrentUserResource;
  pricingStore: PricingStore;
  loadingQuotations: boolean;
  quotationResource: QuotationResource;
  loadingEnrolmentInfo: boolean;
  enrolmentInfo: {
    existing_subscription: boolean;
    enrolment_id: number;
  };
}

class Wrapper extends Component<Props, {}> {
  renderPricing = (state) => {
    const { course } = this.props;

    switch (state.kind) {
      case "ready":
        return (
          <NewPricing
            isCoursePricing={true}
            handleShowSignInSideBar={this.props.handleShowSignInSideBar}
            pricing={state.pricing}
            singleCourseHeading={course.name}
            updateStyle={true}
            showSigninSidebar={
              this.props.currentUser.kind === "current-user-absent"
            }
            courseSlug={course.slug}
          />
        );
      case "loading":
        return (
          <div
            className="pricing ui vertical center aligned segment"
            id="pricing-container"
          >
            <div className="ui active inverted dimmer">
              <div className="ui text loader">Loading</div>
            </div>
          </div>
        );
      default:
        return <div />;
    }
  };

  renderPurchaseNavbar = () => {
    const {
      course,
      currentUser,
      handleShowSignInSideBar,
      quotationResource,
      loadingQuotations,
      loadingEnrolmentInfo,
      enrolmentInfo,
    } = this.props;

    // The price bar always renders. It used to be hidden by a hidePreviewNavbar
    // flag, which read a cookie that was set on nearly every request and so was
    // almost always false. Wiring that flag to "a banner is live" made it true
    // whenever a campaign ran and took this bar off every course page. The two
    // stack now — see body.course-has-promo-bar in promoBar.scss — so the flag
    // has no job left and is gone.
    if (!quotationResource) {
      return null;
    }
    const { pricingState } = this.props.pricingStore;

    return (
      <PurchaseNavbar
        course={course}
        currentUser={currentUser}
        quotationResource={quotationResource}
        loadingQuotations={loadingQuotations}
        loadingEnrolmentInfo={loadingEnrolmentInfo}
        enrolmentInfo={enrolmentInfo}
        handleShowSignInSideBar={handleShowSignInSideBar}
        showSigninSidebar={currentUser.kind === "current-user-absent"}
        pricingState={pricingState}
      />
    );
  };

  render() {
    const {
      mode,
      refresh,
      course,
      currentUser,
      handleShowSignInSideBar,
      pricingStore,
      quotationResource,
      loadingQuotations,
      loadingEnrolmentInfo,
      enrolmentInfo,
    } = this.props;
    const { pricingState } = pricingStore;
    const isUpcoming = mode === "upcoming";
    const upcomingHasLessons = (course?.total_questions_count || 0) > 0;

    return (
      <React.Fragment>
        {isUpcoming ? (
          <PurchaseNavbar
            course={course}
            currentUser={currentUser}
            quotationResource={quotationResource}
            loadingQuotations={loadingQuotations}
            loadingEnrolmentInfo={loadingEnrolmentInfo}
            enrolmentInfo={enrolmentInfo}
            handleShowSignInSideBar={handleShowSignInSideBar}
            showSigninSidebar={currentUser.kind === "current-user-absent"}
            mode={mode}
          />
        ) : (
          this.renderPurchaseNavbar()
        )}
        {!enrolmentInfo?.existing_subscription && (
          <PromoBar currentUser={currentUser} pageContext="course" />
        )}
        <main
          className="facelift-landing-page facelift-course-preview"
          id="main"
        >
          <Landing
            mode={mode}
            refresh={refresh}
            course={course}
            currentUser={currentUser}
            quotationResource={quotationResource}
            loadingQuotations={loadingQuotations}
            loadingEnrolmentInfo={loadingEnrolmentInfo}
            enrolmentInfo={enrolmentInfo}
            handleShowSignInSideBar={handleShowSignInSideBar}
            showSigninSidebar={currentUser.kind === "current-user-absent"}
            pricingState={pricingState}
          />
          <HeroTrustStrip />
          <ThoughtLeaders courseSlug={course?.slug} />
          <Promise />
          <Description course={course} />
          <Proof courseName={course.name} />
          {(!isUpcoming || upcomingHasLessons) && (
            <>
              <CourseChapters
                topics={course?.topics}
                courseSlug={course?.slug}
                course={course}
              />
            </>
          )}
          {!isUpcoming && (
            <>
              <Compare />
              <Guarantee />
            </>
          )}
          {this.props.mode !== "upcoming" && (
            <>
              <Reviews rating={course?.rating} courseSlug={course?.slug} />
            </>
          )}
          {this.props.mode !== "upcoming" && (
            <>{this.renderPricing(pricingState)}</>
          )}
          {BEHAVIOURAL_COURSES.includes(course?.slug) && (
            <div className="rq rq--embed">
              <SignupBox variant="course" />
            </div>
          )}
          <Faq />
          <FinalCta mode={mode} courseName={course.name} />
          <FeaturedCourses
            courseSlug={course?.slug}
            sectionTitle={isUpcoming ? "While you wait" : undefined}
            userIsInUk={currentUser.is_in_uk}
          />
        </main>
      </React.Fragment>
    );
  }
}

export default observer(Wrapper);
